import { useCallback } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Button, Tooltip, cn } from "rizzui";
import { toast } from "sonner";
import DeletePopover from "../../../components/buttons/DeletePopover";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { FiltersProvider } from "../../../components/providers/FiltersProvider";
import useOrderStatuses from "../../../hooks/api-calls/orders/useOrderStatuses";
import useKoReasons from "../../../hooks/api-calls/useKoReasons";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import PageHeader from "../../../layout/shared/page-header";
import { menuRoutes } from "../../../router/menu";
import { OrderService } from "../../../services/order/orderService";
import { OrderApiResponse } from "../../../type/entities/order-type";
import OrderInfoSidebar from "./OrderInfoSidebar";

const OrderProfileWrapper = () => {

    // HOOKS

    const { id = '' } = useParams<{ id: string }>();
    const { userCan, isFacilitator, isFacilitatorManager } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const { getOrderStatusesList } = useOrderStatuses();
    const { koReasonsOptions } = useKoReasons();
    const canViewOrderStatus = userCan('change_orders_status', 'orders');
    const isFacilitatorOrderView = isFacilitator || isFacilitatorManager;

    const [data] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new OrderService()).getOrderById(id);
        return response.getResponseData() as OrderApiResponse;
    }, [id]));

    const pageHeader = {
        title: `Datos del Pedido ${data?.code ? `(${data.code})` : ''}`,
        breadcrumb: [
            { name: 'Ventas' },
            { name: 'Gestión de pedidos' },
            { name: 'Listado de pedidos', href: menuRoutes.orders.path + menuRoutes.orders.list },
            { name: 'Detalles del Pedido' },
        ],
    };

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CONTROLA LA ELIMINACIÓN DE UN PEDIDO
     * @EN CONTROLS THE DELETION OF AN ORDER
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await (new OrderService()).deleteOrder(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.orders.path + menuRoutes.orders.list);
                toast.success('Pedido eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CONTROLA EL CAMBIO DE ESTADO DE UN PEDIDO
     * @EN CONTROLS THE CHANGE OF ORDER STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleChangeStatus = async (orderId: string, statusId: string, _comment?: string, koReasonId?: string) => {
        try {
            const response = await (await (new OrderService).toggleOrderState(orderId, statusId, undefined, koReasonId)).getResponseData();
            if (response.success) {
                toast.success('Estado del pedido cambiado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className={cn('flex flex-col sm:flex-row flex-wrap gap-3 mt-3 md:mt-0 px-3')}>
                    {!isFacilitatorOrderView && (data?.id && data?.currentStatus && data?.currentStatusDate) && (
                        <div className='flex flex-col gap-2 justify-content-center text-center' key={data?.id + data?.currentStatus.id}>
                            <StatusDropdown
                                entityId={data?.id}
                                title="Se cambiará el estado del pedido"
                                statesOptions={getOrderStatusesList()}
                                currentState={data?.currentStatus}
                                disabled={!canViewOrderStatus}
                                currentStateDate={data?.currentStatusDate?.date}
                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) => handleChangeStatus(data?.id, statusId, comment, koReasonId)}
                                hasComments={false}
                                badgeSize={'sm'}
                                koReasonsOptions={koReasonsOptions}
                                koReasonStatusEntity="order"
                            />
                        </div>
                    )}

                    {!isFacilitatorOrderView && userCan('edit_orders', 'orders') && (
                        <Tooltip content={'Editar pedido'} placement={'top'} color={'invert'} size={'md'}>
                            <Button variant={'outline'} color={'primary'} size={'md'} onClick={() => {
                                navigate(`${menuRoutes.orders.path}/${id}/edit`)
                            }}>
                                Editar
                            </Button>
                        </Tooltip>
                    )}

                    {!isFacilitatorOrderView && userCan('delete_orders', 'orders') && (
                        <Tooltip content={'Eliminar pedido'} placement={'top'} color={'invert'} size={'md'} className='text-danger'>
                            <DeletePopover
                                title={`Eliminar pedido`}
                                description={`¿Estás seguro de que deseas eliminar el pedido?`}
                                size={'md'}
                                onDelete={() => handleDelete(id)}
                                actionIconClassName='h-auto w-10 h-10 m-auto sm:m-0'
                            />
                        </Tooltip>
                    )}
                </div>
            </PageHeader>

            <div className="flex flex-grow flex-col 3xl:pt-4 4xl:px-10 animate-slide-down">
                <div className="page-container flex w-full flex-col flex-nowrap lg:flex-row">
                    <div className="sidebar w-full overflow-hidden lg:w-[20vw] lg:shrink-0">
                        <OrderInfoSidebar />
                    </div>
                    <div className="content h-full w-full min-w-0 flex-1 p-4 pt-0">
                        <FiltersProvider>
                            <Outlet />
                        </FiltersProvider>
                    </div>
                </div>
            </div>
        </>
    );
}

export default OrderProfileWrapper;
